import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';

/**
 * A dialog that plays the selected variant's video and stops it when closed.
 *
 * Extends the base dialog so it inherits open/close, outside-click and Escape
 * handling; adds playback control so the video starts on open and is paused and
 * reset on close (native <video> keeps playing otherwise), plus a loading
 * indicator shown until playback actually begins or while it stalls.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLVideoElement} [video] - The video element.
 * @property {HTMLElement} [loader] - The loading overlay.
 *
 * @extends DialogComponent<Refs>
 */
class ProductVideoDialog extends DialogComponent {
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    this.addEventListener(DialogOpenEvent.eventName, this.#handleOpen, { signal });
    this.addEventListener(DialogCloseEvent.eventName, this.#handleClose, { signal });

    const video = this.#video;
    if (video) {
      // 'playing' fires once frames are actually rendering; 'waiting' on a stall.
      video.addEventListener('playing', this.#hideLoader, { signal });
      video.addEventListener('waiting', this.#showLoader, { signal });
      video.addEventListener('error', this.#hideLoader, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /** @returns {HTMLVideoElement | null} */
  get #video() {
    const { video } = this.refs;
    return video instanceof HTMLVideoElement ? video : null;
  }

  #handleOpen = () => {
    const video = this.#video;
    if (!video) return;

    // Show the loader unless the video already has enough buffered to play through.
    if (video.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) this.#showLoader();

    const playback = video.play();
    // Ignore autoplay rejections (e.g. before user interaction); controls stay usable.
    if (playback) playback.catch(this.#hideLoader);
  };

  #handleClose = () => {
    const video = this.#video;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
    this.#hideLoader();
  };

  #showLoader = () => {
    this.refs.loader?.removeAttribute('hidden');
  };

  #hideLoader = () => {
    this.refs.loader?.setAttribute('hidden', '');
  };
}

if (!customElements.get('product-video-dialog')) {
  customElements.define('product-video-dialog', ProductVideoDialog);
}
